import { QueryResponse } from 'types/paginationAndFilter';
import { portalAPI } from './portalAPI';

export type ProcessType =
    | 'CLIENT_BLOCKING'
    | 'CLIENT_BLOCKING_WARNING'
    | 'INVOICE_AUTOGENERATION'
    | 'NETVISOR_EXPENSE_POLL'
    | 'NETVISOR_SALES_INVOICE_IMPORT'
    | 'NETVISOR_PAYMENT_POLL'
    | 'NETVISOR_DIMENSION_SYNC'
    | 'NETVISOR_PARTY_SYNC'
    | 'COMPLIANCE_EVALUATION'
    | 'SAGE_HR_SYNC'
    | 'WINTIME_EXPENSE_IMPORT'
    | 'SMAILY_SINGLE_PROFILE_SYNC'
    | 'SMAILY_PROFILES_SYNC'
    | 'SMAILY_SUBSCRIBER_CLEANUP'
    | 'SMAILY_OPT_OUT_STATUS_SYNC'
    | 'WORDPRESS_EMPLOYEE_SYNC'
    | 'WORDPRESS_SINGLE_EMPLOYEE_SYNC'
    | 'INSIDER_FUNCTION_AND_REASON_FILL';

export type ProcessStatuses = Partial<Record<ProcessType, boolean>>;

interface ProcessExecutionDto {
    id: number;
    processTypeCode: ProcessType;
    startedAt: string;
    finishedAt: string | null;
    success: boolean | null;
    errorMessage: string | null;
    createdBy: { id: number; name: string } | null;
    running?: boolean;
    totalItems: number;
    processedItems: number;
    skippedItems: number;
    failedItems: number;
}

export interface ProcessExecutionItemDto {
    id: number;
    itemType: string;
    externalKey: string;
    internalId: number | null;
    status: string;
    errorCode: string | null;
    errorMessage: string | null;
    errorDetail: string | null;
    retryCount: number;
    lastRetryAt: string | null;
}

interface ProcessHistoryParams {
    processType?: ProcessType;
    page: number;
    size: number;
    sort?: string;
}

interface ProcessItemsParams {
    executionId: number;
    status?: string;
    page: number;
    size: number;
}

const APIProcessesSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['ProcessStatuses', 'ProcessHistory', 'ProcessItems'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            runProcess: builder.mutation<void, ProcessType>({
                query: (processType) => ({
                    url: '/v1/processes/run',
                    method: 'POST',
                    params: { processType },
                }),
                invalidatesTags: ['ProcessStatuses', 'ProcessHistory'],
            }),
            getProcessStatuses: builder.query<ProcessStatuses, void>({
                query: () => '/v1/processes/status',
                providesTags: ['ProcessStatuses'],
            }),
            getProcessHistory: builder.query<QueryResponse<ProcessExecutionDto>, ProcessHistoryParams>({
                query: ({ processType, page, size, sort = 'startedAt,desc' }) => ({
                    url: '/v1/processes/history',
                    params: {
                        ...(processType ? { processType } : {}),
                        page,
                        size,
                        sort,
                    },
                }),
                providesTags: ['ProcessHistory'],
            }),
            getProcessExecutionItems: builder.query<QueryResponse<ProcessExecutionItemDto>, ProcessItemsParams>({
                query: ({ executionId, status, page, size }) => ({
                    url: `/v1/processes/${executionId}/items`,
                    params: {
                        ...(status ? { status } : {}),
                        page,
                        size,
                    },
                }),
                providesTags: ['ProcessItems'],
            }),
            retryProcessExecutionItem: builder.mutation<
                ProcessExecutionItemDto,
                { executionId: number; itemId: number }
            >({
                query: ({ executionId, itemId }) => ({
                    url: `/v1/processes/${executionId}/items/${itemId}/retry`,
                    method: 'POST',
                }),
                invalidatesTags: ['ProcessItems', 'ProcessHistory', 'ProcessStatuses'],
            }),
        }),
        overrideExisting: false,
    });

export const {
    useRunProcessMutation,
    useGetProcessStatusesQuery,
    useLazyGetProcessStatusesQuery,
    useLazyGetProcessHistoryQuery,
    useGetProcessExecutionItemsQuery,
    useRetryProcessExecutionItemMutation,
} = APIProcessesSlice;
